import { action, computed, makeObservable, observable } from 'mobx';
import { Award, AwardJudge, AwardLead, AwardNominationReviewer, AwardNominationSummary, AwardStatistic, BatchCompletionSummary, batchCompletionSummary, BatchProgressSnapshot, BatchProgressStore, batchProcessedCount, blankAwardJudge, DEFAULT_MAX_FINALISTS, DEFAULT_MAX_WINNERS, DropdownOption, isBatchUiTerminal, NominationRole } from '../../models';

export enum AwardTabTypeCode {
  Nominations = 'nominations',
  Details = 'details',
  AiEval = 'ai-eval',
}

export class AwardPageUiStore implements BatchProgressStore {
  @observable selectedAward?: Award;

  @observable awardStatistic?: AwardStatistic;

  @observable awardNominationSummaries: AwardNominationSummary[] = [];

  @observable awardNominationReviewers: AwardNominationReviewer[] = [];

  @observable awardLeads: AwardLead[] = [];

  @observable awardJudges: AwardJudge[] = [];

  @observable isShowJudgeAssignmentModal = false;

  @observable pendingAwardJudges: AwardJudge[] = [];

  @observable judgeCapacityOptions: DropdownOption<number>[] = [];

  @observable selectedJudgeCapacityOptionValue?: number;

  @observable selectedAwardNominationSummary?: AwardNominationSummary;

  @observable selectedNominationRole?: NominationRole;

  @observable selectedAwardTabTypeCode: AwardTabTypeCode = AwardTabTypeCode.Nominations;

  @observable activeBatchRunId?: string;

  @observable batchProgress?: BatchProgressSnapshot;

  @observable isBatchStreaming = false;

  @observable isEvaluationRun?: boolean;

  @observable queuePosition?: number;

  @observable queueDepth?: number; // threaded from the DTO; intentionally stored-not-displayed

  @observable lastBatchSummary?: BatchCompletionSummary;

  constructor() {
    makeObservable(this);
  }

  @action
  resetStates() {
    this.selectedAward = undefined;
    this.awardStatistic = undefined;
    this.awardNominationSummaries = [];
    this.awardNominationReviewers = [];
    this.awardLeads = [];
    this.awardJudges = [];
    this.isShowJudgeAssignmentModal = false;
    this.pendingAwardJudges = [];
    this.judgeCapacityOptions = [];
    this.selectedJudgeCapacityOptionValue = undefined;
    this.selectedAwardNominationSummary = undefined;
    this.selectedNominationRole = undefined;
    this.selectedAwardTabTypeCode = AwardTabTypeCode.Nominations;
    this.activeBatchRunId = undefined;
    this.batchProgress = undefined;
    this.isBatchStreaming = false;
    this.isEvaluationRun = undefined;
    this.queuePosition = undefined;
    this.queueDepth = undefined;
    this.lastBatchSummary = undefined;
  }

  /* AI Scrubbing Batch */

  @action
  setActiveBatchRunId(batchRunId?: string): void {
    this.activeBatchRunId = batchRunId;
    if (batchRunId) this.lastBatchSummary = undefined; // a new batch clears the prior breakdown
  }

  @action
  setBatchProgress(batchProgress?: BatchProgressSnapshot): void {
    this.batchProgress = batchProgress;
  }

  @action
  setQueueMetrics(position?: number, depth?: number): void {
    this.queuePosition = position;
    this.queueDepth = depth;
  }

  @action
  setIsBatchStreaming(isBatchStreaming: boolean): void {
    this.isBatchStreaming = isBatchStreaming;
  }

  @action
  setIsEvaluationRun(isEvaluation?: boolean): void {
    this.isEvaluationRun = isEvaluation;
  }

  @action
  clearBatchProgress(): void {
    this.activeBatchRunId = undefined;
    this.batchProgress = undefined;
    this.isBatchStreaming = false;
    this.isEvaluationRun = undefined;
    this.queuePosition = undefined;
    this.queueDepth = undefined;
  }

  @action
  finalizeBatch(): void {
    if (this.batchProgress) {
      this.lastBatchSummary = batchCompletionSummary(this.batchProgress, this.isEvaluationRun ?? false);
    }
    this.clearBatchProgress();
  }

  @action
  clearLastBatchSummary(): void {
    this.lastBatchSummary = undefined;
  }

  @computed
  get isBatchInProgress(): boolean {
    return !!this.activeBatchRunId && !isBatchUiTerminal(this.batchProgress?.state);
  }

  @computed
  get batchProgressLabel(): string {
    const progress = this.batchProgress;
    if (progress?.state === 'Queued') {
      if (this.queuePosition == null) return 'Queued - waiting to start...';
      if (this.queuePosition === 1) return 'Queued - next to run';
      const ahead = this.queuePosition - 1;
      return `Queued - waiting behind ${ahead} batch${ahead === 1 ? '' : 'es'}`;
    }
    return progress ? `Processing ${batchProcessedCount(progress)}/${progress.total}...` : 'Processing...';
  }

  /* Award */

  @action
  setSelectedAward(award: Award): void {
    this.selectedAward = award;
    this.selectedJudgeCapacityOptionValue = award.judgeCapacity;
  }

  @action
  setAwardNominationSummaries(awardNominationSummaries: AwardNominationSummary[]): void {
    this.awardNominationSummaries = awardNominationSummaries;
  }

  @action
  setAwardLeads(awardLeads: AwardLead[]): void {
    this.awardLeads = awardLeads;
  }

  @action
  setJudgeCapacityOptions(options: DropdownOption<number>[]): void {
    this.judgeCapacityOptions = options;
  }

  @action
  setSelectedJudgeCapacityOptionValue(value: string): void {
    this.selectedJudgeCapacityOptionValue = Number(value);
  }

  @computed
  get selectedJudgeCapacityOptions(): string[] {
    if (!this.selectedJudgeCapacityOptionValue) return [];
    return [String(this.selectedJudgeCapacityOptionValue)];
  }

  @computed
  get selectedJudgeCapacityValue(): string {
    if (!this.selectedJudgeCapacityOptionValue) return '';
    return this.judgeCapacityOptions.find((option) => option.value === this.selectedJudgeCapacityOptionValue)?.text ?? '';
  }

  @action
  setAwardNominationReviewers(awardNominationReviewers: AwardNominationReviewer[]): void {
    this.awardNominationReviewers = awardNominationReviewers;
  }

  @action
  setAwardJudges(awardJudges: AwardJudge[]): void {
    this.awardJudges = awardJudges;
  }

  @action
  addAwardJudge(judge: AwardJudge): void {
    this.awardJudges.push(judge);
  }

  @action
  addAwardJudges(judges: AwardJudge[]): void {
    this.awardJudges.push(...judges);
  }

  @action
  removeAwardJudge(judge: AwardJudge): void {
    const index = this.awardJudges.indexOf(judge);
    if (index >= 0) {
      this.awardJudges.splice(index, 1);
    }
  }

  @action
  setAwardJudgeStatus(judge: AwardJudge, status: boolean): void {
    const index = this.awardJudges.indexOf(judge);
    if (index >= 0) {
      this.awardJudges[index].judgingStarted = status;
    }
  }

  @action
  setShowJudgeAssignmentModal(isShow: boolean): void {
    this.isShowJudgeAssignmentModal = isShow;
  }

  @action
  addPendingAwardJudges(emails: string[], awardId: number): void {
    const judges = emails.map((email) => {
      const judge = blankAwardJudge();
      judge.awardId = awardId;
      judge.emailAddress = email;
      return judge;
    });
    this.pendingAwardJudges.push(...judges);
  }

  @action
  clearPendingAwardJudges(): void {
    this.pendingAwardJudges = [];
  }

  @action
  removePendingAwardJudge(judge: AwardJudge): void {
    const index = this.pendingAwardJudges.indexOf(judge);
    if (index >= 0) {
      this.pendingAwardJudges.splice(index, 1);
    }
  }

  @action
  setPendingAwardJudgeStatus(judge: AwardJudge, status: boolean): void {
    const index = this.pendingAwardJudges.indexOf(judge);
    if (index >= 0) {
      this.pendingAwardJudges[index].judgingStarted = status;
    }
  }

  @computed
  get activeJudgesCount(): number {
    return this.awardJudges.filter((judge) => judge.judgingStarted).length;
  }

  @computed
  get inactiveJudgesCount(): number {
    return this.awardJudges.filter((judge) => !judge.judgingStarted).length;
  }

  @action
  setSelectedAwardNominationSummary(awardNominationSummary?: AwardNominationSummary): void {
    this.selectedAwardNominationSummary = awardNominationSummary;
  }

  @action
  setSelectedNominationRole(nominationRole?: NominationRole): void {
    this.selectedNominationRole = nominationRole;
  }

  @computed
  get isWinnerAssigned(): boolean {
    const otherWinners = this.awardNominationSummaries.filter(
      (summary) => summary.role === 'Winner' && summary.nominationId !== this.selectedAwardNominationSummary?.nominationId,
    );
    return otherWinners.length >= (this.selectedAward?.maxWinners ?? DEFAULT_MAX_WINNERS);
  }

  @computed
  get isFinalistAssigned(): boolean {
    const otherFinalists = this.awardNominationSummaries.filter(
      (summary) => summary.role === 'Finalist' && summary.nominationId !== this.selectedAwardNominationSummary?.nominationId,
    );
    return otherFinalists.length >= (this.selectedAward?.maxFinalists ?? DEFAULT_MAX_FINALISTS);
  }

  /* Tab */

  @action
  setSelectedAwardTabTypeCode(tabTypeCode: AwardTabTypeCode): void {
    this.selectedAwardTabTypeCode = tabTypeCode;
  }

  @computed
  get activeAwardTabTypeCodes(): AwardTabTypeCode[] {
    return [AwardTabTypeCode.Nominations, AwardTabTypeCode.Details];
  }

  /* Statistic */

  @action
  setAwardStatistic(awardStatistic: AwardStatistic): void {
    this.awardStatistic = awardStatistic;
  }

  @computed
  get submittedNominationsCount(): number {
    return this.awardStatistic?.numberOfSubmittedNominations ?? 0;
  }

  @computed
  get incompleteNominationsCount(): number {
    return this.awardStatistic?.numberOfIncompleteNominations ?? 0;
  }

  @computed
  get passedNominationsCount(): number {
    return this.awardStatistic?.numberOfPassedNominations ?? 0;
  }

  @computed
  get disqualifiedNominationsCount(): number {
    return this.awardStatistic?.numberOfDisqualifiedNominations ?? 0;
  }

  @computed
  get pendingAffirmationNominationsCount(): number {
    return this.awardStatistic?.numberOfPendingAffirmationNominations ?? 0;
  }

  @computed
  get judgesActiveCount(): number {
    return this.awardStatistic?.numberOfJudgesActive ?? 0;
  }

  @computed
  get judgesInActiveCount(): number {
    return this.awardStatistic?.numberOfJudgesInActive ?? 0;
  }

  @computed
  get judgedNominationsCompletedCount(): number {
    return this.awardStatistic?.numberOfJudgedNominationsCompleted ?? 0;
  }

  @computed
  get judgedNominationsInProgressCount(): number {
    return this.awardStatistic?.numberOfJudgedNominationsInProgress ?? 0;
  }
}
